import { Theme } from "@material-ui/core/styles";

const getTableStyles = (theme: Theme) => ({
  '& [class*="MuiTable-root"]': {
    backgroundColor: theme.palette.background.paper,
    borderCollapse: "separate",
    borderSpacing: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    overflow: "hidden",
  },

  '& [class*="MuiTableHead-root"]': {
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  '& [class*="MuiTableRow-root"][class*="MuiTableRow-head"]': {
    backgroundColor: theme.palette.background.default,
  },

  '& [class*="MuiTableCell-root"][class*="MuiTableCell-head"]': {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: "0.8125rem",
    textTransform: "none",
    letterSpacing: 0,
    padding: theme.spacing(2, 3),
    minWidth: theme.spacing(15),
    borderBottom: "none",
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    lineHeight: "1.5",
  },

  '& [class*="MuiTableBody-root"]': {
    backgroundColor: theme.palette.background.paper,
  },

  '& [class*="MuiTableRow-root"]:not([class*="MuiTableRow-head"])': {
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create("background-color", {
      duration: theme.transitions.duration.shortest,
    }),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },

  '& [class*="MuiTableBody-root"] [class*="MuiTableRow-root"]': {
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create("background-color", {
      duration: theme.transitions.duration.shortest,
    }),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },

  '& [class*="MuiTableCell-root"][class*="MuiTableCell-body"]': {
    color: theme.palette.text.primary,
    fontSize: "0.875rem",
    padding: theme.spacing(2.5, 3),
    minWidth: theme.spacing(15),
    maxWidth: theme.spacing(25),
    borderBottom: `0.3px solid ${theme.palette.grey[300]}`,
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    lineHeight: "1.5",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  '& [class*="MuiTableCell-body"]': {
    color: theme.palette.text.primary,
    fontSize: "0.875rem",
    padding: theme.spacing(2.5, 3),
    lineHeight: "1.5",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

const getTableToolbarStyles = (theme: Theme) => ({
  '& [class*="BackstageTableToolbar-root"]': {
    backgroundColor: "transparent",
    background: "transparent",
    boxShadow: "none",
    borderBottom: "none",
    padding: theme.spacing(0, 0, 2, 0),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
});

const getTransparentPaperStyles = () => ({
  '& [class*="MuiPaper-root"]': {
    backgroundColor: "transparent",
    background: "transparent",
    boxShadow: "none",
  },
});

const getTableSortLabelStyles = (theme: Theme) => ({
  '& [class*="MuiTableSortLabel-root"]': {
    color: "inherit",
    "&:hover": {
      color: theme.palette.text.primary,
    },
    "&:focus": {
      color: theme.palette.text.primary,
    },
  },

  '& [class*="MuiTableSortLabel-icon"]': {
    opacity: 0,
    fontSize: "1rem",
    transition: theme.transitions.create("opacity", {
      duration: theme.transitions.duration.shorter,
    }),
  },

  '& [class*="MuiTableCell-head"]:hover [class*="MuiTableSortLabel-icon"]': {
    opacity: "0.5",
  },

  '& [class*="MuiTableSortLabel-root"]:hover [class*="MuiTableSortLabel-icon"]':
    {
      opacity: "0.5",
    },

  '& [class*="MuiTableSortLabel-active"] [class*="MuiTableSortLabel-icon"]': {
    opacity: "1",
  },
});

const getTableActionButtonStyles = (theme: Theme) => ({
  '& [class*="MuiIconButton-root"]': {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.75),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },

  '& [class*="MuiTableCell-paddingNone"]': {
    "& > div": {
      display: "flex !important",
      justifyContent: "center !important",
    },
  },

  '& td[class*="MuiTableCell"] > div[style*="display: flex"]': {
    justifyContent: "center !important",
  },

  '& th[class*="MuiTableCell-paddingCheckbox"]': {
    textAlign: "center !important",
  },
});

const getEntityDisplayStyles = (theme: Theme) => ({
  '& [class*="MuiTable-root"] [class*="CatalogReactEntityDisplayName-root"]': {
    fontSize: "0.875rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },

  '& [class*="MuiTable-root"] [class*="CatalogReactEntityDisplayName-icon"]': {
    width: theme.spacing(3),
    height: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  '& [class*="MuiTable-root"] [class*="BackstageSubvalueCell-value"]': {
    fontSize: "0.875rem",
    fontWeight: theme.typography.fontWeightMedium,
  },

  '& [class*="MuiTable-root"] [class*="BackstageSubvalueCell-subvalue"]': {
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.5),
  },

  '& [class*="MuiTable-root"] [class*="BackstageOverflowTooltip-typo"]': {
    fontSize: "inherit",
    lineHeight: "inherit",
  },
});

const getLinkStyles = (theme: Theme) => ({
  '& a[href], & [class*="MuiLink"]': {
    color: theme.palette.primary.dark,
    textDecoration: "none",
    transition: theme.transitions.create(["color"], {
      duration: theme.transitions.duration.shortest,
    }),
    "&:hover": {
      color: "#0D47A1", // Even darker blue for hover
      textDecoration: "none",
    },
  },
});

export const getCompleteTableStyles = (theme: Theme) => ({
  ...getTableStyles(theme),
  ...getTableToolbarStyles(theme),
  ...getTransparentPaperStyles(),
  ...getEntityDisplayStyles(theme),
  ...getTableActionButtonStyles(theme),
  ...getTableSortLabelStyles(theme),
  ...getLinkStyles(theme),
});
